import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getKb } from "../lib/api";
import { useApi } from "../lib/useApi";
import { EmptyState, ErrorState, Loading } from "../components/States";
import type { KbArticle, KbCategory } from "../lib/types";

/** Knowledge base index. Public (no auth). Lists categories with their published
 *  articles, supports a category filter (?category=slug) and a client-side search. */
export function KbListPage() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") ?? "";
  const [query, setQuery] = useState("");

  // Fetch full KB (categories + all published articles). We request without a
  // category filter so the category rail always shows; filtering is client-side.
  const { data, loading, error, reload } = useApi((signal) => getKb(undefined, signal), []);

  const categories: KbCategory[] = data?.categories ?? [];
  const articles: KbArticle[] = data?.articles ?? [];

  const byCategory = useMemo(() => {
    const map = new Map<string | null, KbArticle[]>();
    for (const a of articles) {
      const key = a.category_id;
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    return map;
  }, [articles]);

  const catBySlug = useMemo(() => {
    const m = new Map<string, KbCategory>();
    for (const c of categories) m.set(c.slug, c);
    return m;
  }, [categories]);

  const q = query.trim().toLowerCase();
  const matches = (a: KbArticle) =>
    !q || a.title.toLowerCase().includes(q) || a.body_md.toLowerCase().includes(q);

  // Resolve the active category object (if the slug is valid).
  const activeCat = activeCategory ? catBySlug.get(activeCategory) : undefined;

  function visibleArticlesFor(categoryId: string | null): KbArticle[] {
    return (byCategory.get(categoryId) ?? []).filter(matches);
  }

  // Articles whose category_id doesn't resolve to a listed category (or is null).
  const uncategorized = useMemo(() => {
    const known = new Set(categories.map((c) => c.id));
    return articles.filter((a) => a.category_id === null || !known.has(a.category_id));
  }, [articles, categories]);

  if (loading) return <Loading label="Loading the knowledge base…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="kb">
      <div className="page-head">
        <div>
          <h1>Knowledge base</h1>
          <p className="sub">Guides and answers to common questions.</p>
        </div>
      </div>

      <div className="kb-toolbar">
        <label className="field grow">
          <span className="sr-only">Search articles</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles…"
            aria-label="Search articles"
          />
        </label>
      </div>

      {categories.length > 0 ? (
        <nav className="kb-cats" aria-label="Categories">
          <button
            className={`chip${activeCategory === "" ? " active" : ""}`}
            onClick={() => setParams({})}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              className={`chip${activeCategory === c.slug ? " active" : ""}`}
              onClick={() => setParams({ category: c.slug })}
            >
              {c.name}
            </button>
          ))}
        </nav>
      ) : null}

      {articles.length === 0 ? (
        <EmptyState title="Nothing here yet">
          <p className="muted">Published articles will appear here.</p>
        </EmptyState>
      ) : (
        <div className="kb-sections">
          {/* When a category is selected, show only it; otherwise show all. */}
          {(activeCat ? [activeCat] : categories).map((c) => {
            const list = visibleArticlesFor(c.id);
            if (list.length === 0) return null;
            return <CategoryBlock key={c.id} name={c.name} articles={list} />;
          })}

          {/* Uncategorized articles only in the unfiltered view. */}
          {!activeCategory && uncategorized.filter(matches).length > 0 ? (
            <CategoryBlock name="Other articles" articles={uncategorized.filter(matches)} />
          ) : null}

          {/* Empty search result. */}
          {q &&
          (activeCat ? visibleArticlesFor(activeCat.id) : articles.filter(matches)).length === 0 ? (
            <EmptyState title="No matches">
              <p className="muted">
                Nothing matched “{query}”. Try a different term or{" "}
                <Link to="/tickets/new">submit a request</Link>.
              </p>
            </EmptyState>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CategoryBlock({ name, articles }: { name: string; articles: KbArticle[] }) {
  return (
    <section className="kb-section">
      <h2 className="kb-section-title">{name}</h2>
      <ul className="kb-article-list">
        {articles.map((a) => (
          <li key={a.id}>
            <Link to={`/kb/${encodeURIComponent(a.slug)}`} className="kb-article-row">
              <span className="kb-article-name">{a.title}</span>
              <span className="kb-article-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
